import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import Translator from '%app/js/common/services/translator';
import { SHORTCUT_KEYS, SHORTCUT_RESOLVERS } from './dateShortcuts';
import TOGGLE_PILL_INTERACTIVE_BASE_SX, { TOGGLE_PILL_SELECTED_BORDER_SX } from '../togglePillSx';
import brandSurfaceInk from '../../services/brandSurfaceInk';
import {
    HIGH_CONTRAST_HOVER_SWAP_SX,
    STRONG_BORDER_COLOR,
    createHighContrastHoverSwapSx,
} from '../highContrastSx';

const SHORTCUTS = Object.freeze([
    Object.freeze({ key: SHORTCUT_KEYS.thisWeekend, labelKey: 'search_panel.shortcuts.this_weekend' }),
    Object.freeze({ key: SHORTCUT_KEYS.today, labelKey: 'search_panel.shortcuts.today' }),
    Object.freeze({ key: SHORTCUT_KEYS.tomorrow, labelKey: 'search_panel.shortcuts.tomorrow' }),
    Object.freeze({ key: SHORTCUT_KEYS.thisMonth, labelKey: 'search_panel.shortcuts.this_month' }),
]);

const QUICK_LABEL_KEY = 'search_panel.shortcuts.label';

const DESKTOP_BAR_SX = {
    display: { xs: 'none', md: 'flex' },
    alignItems: 'center',
    gap: 1,
    mt: 2,
    flexWrap: 'wrap',
    rowGap: 1,
};

const DESKTOP_LABEL_SX = { color: 'grey.400' };

const DESKTOP_PILL_BASE_SX = {
    ...TOGGLE_PILL_INTERACTIVE_BASE_SX,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 36,
    px: 2,
    borderRadius: '999px',
};

const DESKTOP_PILL_SX = {
    ...DESKTOP_PILL_BASE_SX,
    borderColor: STRONG_BORDER_COLOR,
    color: 'grey.600',
    bgcolor: 'background.default',
    '&:hover': {
        borderColor: 'primary.main',
        color: 'primary.main',
    },
};

const DESKTOP_PILL_SELECTED_SX = {
    ...DESKTOP_PILL_BASE_SX,
    ...TOGGLE_PILL_SELECTED_BORDER_SX,
    borderColor: 'primary.main',
    color: 'primary.main',
    bgcolor: 'selection.bg',
};

const MOBILE_BAR_SX = {
    display: { xs: 'flex', md: 'none' },
    alignItems: 'center',
    gap: 1,
    bgcolor: 'brandSurface.main',
    px: 2.5,
    py: 1.5,
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
};

const MOBILE_LABEL_SX = { color: theme => brandSurfaceInk(theme, 0.5), flexShrink: 0 };

const MOBILE_PILL_BASE_SX = {
    ...TOGGLE_PILL_INTERACTIVE_BASE_SX,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    minHeight: 36,
    px: 2,
    borderRadius: '999px',
};

const MOBILE_PILL_SX = {
    ...MOBILE_PILL_BASE_SX,
    borderColor: theme => brandSurfaceInk(theme, 0.25),
    color: 'brandSurface.contrastText',
    bgcolor: 'transparent',
    '&:hover': {
        borderColor: theme => brandSurfaceInk(theme, 0.5),
    },
    '&:active': {
        bgcolor: theme => alpha(theme.palette.brandSurface.contrastText, 0.1),
    },
};

const MOBILE_PILL_HOVER_SWAP_SX = createHighContrastHoverSwapSx('brandSurface.actionHover', 'brandSurface.main');

const MOBILE_PILL_SELECTED_SX = {
    ...MOBILE_PILL_BASE_SX,
    borderColor: 'brandSurface.contrastText',
    color: 'brandSurface.main',
    bgcolor: 'brandSurface.contrastText',
};

function findActiveShortcutKey(dateFrom, dateTo) {
    if (!dateFrom || !dateTo) {
        return null;
    }
    const now = new Date();
    const match = SHORTCUTS.find(({ key }) => {
        const range = SHORTCUT_RESOLVERS[key](now);
        return range.dateFrom === dateFrom && range.dateTo === dateTo;
    });
    return match ? match.key : null;
}

function QuickDateShortcuts({ onSelectRange, dateFrom = '', dateTo = '' }) {
    const handleSelect = key => () => onSelectRange(SHORTCUT_RESOLVERS[key](new Date()));
    const quickLabel = Translator.trans(QUICK_LABEL_KEY);
    const activeKey = findActiveShortcutKey(dateFrom, dateTo);

    return (
        <>
            <Box sx={DESKTOP_BAR_SX} data-testid="search-panel-shortcuts-desktop">
                <Typography component="span" variant="labelS" sx={DESKTOP_LABEL_SX}>
                    {quickLabel}
                </Typography>
                {SHORTCUTS.map((shortcut) => {
                    const isActive = shortcut.key === activeKey;
                    return (
                        <Typography
                            key={shortcut.key}
                            component="button"
                            type="button"
                            variant="labelS"
                            onClick={handleSelect(shortcut.key)}
                            aria-pressed={isActive}
                            data-testid={`search-panel-shortcut-${shortcut.key}`}
                            sx={isActive ? DESKTOP_PILL_SELECTED_SX : [DESKTOP_PILL_SX, HIGH_CONTRAST_HOVER_SWAP_SX]}
                        >
                            {Translator.trans(shortcut.labelKey)}
                        </Typography>
                    );
                })}
            </Box>
            <Box sx={MOBILE_BAR_SX} data-testid="search-panel-shortcuts-mobile">
                <Typography component="span" variant="labelS" sx={MOBILE_LABEL_SX}>
                    {quickLabel}
                </Typography>
                {SHORTCUTS.map((shortcut) => {
                    const isActive = shortcut.key === activeKey;
                    return (
                        <Typography
                            key={shortcut.key}
                            component="button"
                            type="button"
                            variant="labelS"
                            onClick={handleSelect(shortcut.key)}
                            aria-pressed={isActive}
                            data-testid={`search-panel-shortcut-mobile-${shortcut.key}`}
                            sx={isActive ? MOBILE_PILL_SELECTED_SX : [MOBILE_PILL_SX, MOBILE_PILL_HOVER_SWAP_SX]}
                        >
                            {Translator.trans(shortcut.labelKey)}
                        </Typography>
                    );
                })}
            </Box>
        </>
    );
}

QuickDateShortcuts.propTypes = {
    onSelectRange: PropTypes.func.isRequired,
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
};

export default QuickDateShortcuts;
